import { BookOpen, LifeBuoy, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LumiVoiceButton } from "@/components/lumi/LumiVoiceButton";
import { LumiAvatar } from "@/components/lumi/LumiAvatar";
import { SUPPORT_EMAIL, FAQ_ITEMS } from "@/lib/support-center";

const LUMI_SUPPORT_WELCOME =
  "Hi, I’m Lumi. I can help you find answers, browse common questions, or connect you with our support team.";

/**
 * Help & Support entry point. HeirWise no longer ships a third-party messenger
 * widget: support runs entirely through the in-app Support Center ticket flow
 * and the HeirWise support mailbox.
 */
const HelpCenterSection = ({ onOpenTickets }: { onOpenTickets?: () => void }) => {
  return (
    <section className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-black via-[hsl(260_30%_8%)] to-[hsl(225_35%_7%)] p-8 sm:p-10">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />
        <div className="relative space-y-5 max-w-3xl">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-foreground">
            Help &amp; Support
          </h2>
          <div className="flex items-start gap-3 rounded-2xl border border-primary/25 bg-primary/5 p-3 sm:p-4">
            <LumiAvatar size="md" />
            <div className="flex-1 space-y-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Lumi · Learning Companion</p>
              <p className="text-sm leading-relaxed text-foreground/85">{LUMI_SUPPORT_WELCOME}</p>
            </div>
            <LumiVoiceButton text={LUMI_SUPPORT_WELCOME} label="Hear Lumi’s welcome" />
          </div>
          <div className="flex flex-wrap gap-3 pt-1">
            {onOpenTickets && (
              <Button onClick={onOpenTickets} className="rounded-2xl">
                <MessageCircle className="h-4 w-4 mr-2" /> Contact Support
              </Button>
            )}
            <Button asChild type="button" variant="outline" className="rounded-2xl">
              <a href={`mailto:${SUPPORT_EMAIL}`}>
                <LifeBuoy className="h-4 w-4 mr-2" /> Email our team
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Common questions — authored in-app, no third-party help center */}
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Common questions</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {FAQ_ITEMS.map((faq) => (
            <div
              key={faq.question}
              className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur-xl p-4"
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-xl border border-border/60 bg-background/60 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-foreground">{faq.question}</p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HelpCenterSection;
